import axios from "axios";
import {
  GET_WALLET_BALANCE_REQUEST,
  GET_WALLET_BALANCE_SUCCESS,
  GET_WALLET_BALANCE_FAIL,
  GET_USER_TRANSACTIONS_REQUEST,
  GET_USER_TRANSACTIONS_SUCCESS,
  GET_USER_TRANSACTIONS_FAIL,
  CREATE_DEPOSIT_REQUEST,
  CREATE_DEPOSIT_SUCCESS,
  CREATE_DEPOSIT_FAIL,
  CREATE_WITHDRAW_REQUEST,
  CREATE_WITHDRAW_SUCCESS,
  CREATE_WITHDRAW_FAIL,
  CREATE_BUYIN_REQUEST,
  CREATE_BUYIN_SUCCESS,
  CREATE_BUYIN_FAIL,
  CREATE_CASHOUT_REQUEST,
  CREATE_CASHOUT_SUCCESS,
  CREATE_CASHOUT_FAIL,
  GET_ALL_TRANSACTIONS_REQUEST,
  GET_ALL_TRANSACTIONS_SUCCESS,
  GET_ALL_TRANSACTIONS_FAIL,
  UPDATE_TRANSACTION_STATUS_REQUEST,
  UPDATE_TRANSACTION_STATUS_SUCCESS,
  UPDATE_TRANSACTION_STATUS_FAIL,
  CREATE_ADMIN_TRANSACTION_REQUEST,
  CREATE_ADMIN_TRANSACTION_SUCCESS,
  CREATE_ADMIN_TRANSACTION_FAIL,
  GET_USERS_FOR_TRANSACTION_REQUEST,
  GET_USERS_FOR_TRANSACTION_SUCCESS,
  GET_USERS_FOR_TRANSACTION_FAIL,
  CREATE_CHIP_DEPOSIT_REQUEST,
  CREATE_CHIP_DEPOSIT_SUCCESS,
  CREATE_CHIP_DEPOSIT_FAIL,
  GET_CLUB_LEDGER_REQUEST,
  GET_CLUB_LEDGER_SUCCESS,
  GET_CLUB_LEDGER_FAIL,
} from "../constants/walletConstants";
import { logout } from "./userActions";

const baseUrl = process.env.REACT_APP_API_URL;

// Helper function to get auth token from either adminLogin or userLogin
const getAuthToken = (getState) => {
  const { adminLogin, userLogin } = getState();
  // Prioritize adminLogin (for staff/cashiers), fallback to userLogin (for regular users)
  const userInfo = adminLogin?.userInfo || userLogin?.userInfo;
  return userInfo?.token;
};

// Helper function to get auth config
const getAuthConfig = (getState) => {
  const token = getAuthToken(getState);
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
};

// Add this action to walletActions.js
export const getUserBalanceById = (userId) => async (dispatch, getState) => {
  try {
    const config = getAuthConfig(getState);
    const { data } = await axios.get(
      `${baseUrl}/wallet/user-balance/${userId}`,
      config,
    );
    return data;
  } catch (error) {
    throw error.response?.data?.message || error.message;
  }
};

export const getWalletSummary =
  (filters = {}) =>
  async (dispatch, getState) => {
    try {
      const config = getAuthConfig(getState);
      let url = `${baseUrl}/wallet/admin/wallet-summary?`;
      if (filters.userId) url += `userId=${filters.userId}&`;
      if (filters.clubId) url += `clubId=${filters.clubId}&`;

      // Support date filtering if needed later, but for now specific user/club

      const { data } = await axios.get(url, config);
      return data;
    } catch (error) {
      console.error("Error fetching wallet summary:", error);
      return { success: false, summary: {} };
    }
  };

// Spend settled from player wallets (table time, food & drink, membership, event entry)
// `startDate`/`endDate`: optional ISO timestamps, both inclusive
const dateRangeParams = ({ startDate, endDate } = {}) =>
  (startDate ? `&startDate=${encodeURIComponent(startDate)}` : "") +
  (endDate ? `&endDate=${encodeURIComponent(endDate)}` : "");

export const getSpendTransactions =
  (types = [], limit = 500, dates = {}) =>
  async (dispatch, getState) => {
    try {
      const config = getAuthConfig(getState);
      const { data } = await axios.get(
        `${baseUrl}/wallet/admin/transactions?page=1&limit=${limit}&type=${types.join(",")}${dateRangeParams(dates)}`,
        config,
      );
      return data;
    } catch (error) {
      console.error("Error fetching spend transactions:", error);
      return { success: false, transactions: [] };
    }
  };

// Table bills without a wallet row of their own: unpaid ones and ones paid in cash
export const getTableBills =
  (dates = {}) =>
  async (dispatch, getState) => {
    try {
      const config = getAuthConfig(getState);
      const { data } = await axios.get(
        `${baseUrl}/table-bills?${dateRangeParams(dates).slice(1)}`,
        config,
      );
      return data;
    } catch (error) {
      console.error("Error fetching table bills:", error);
      return { success: false, bills: [] };
    }
  };

// Charge spends to a player: debits the total of `items` ([{ transactionType, amount }])
// from the player's wallet as one spend (one invoice). `paymentMode`: 'wallet',
// 'cash' (collected at the desk), 'deposit' (cash `depositAmount` into the wallet
// first) or 'unpaid' (settled later).
export const createAdminSpend =
  ({ userId, items, clubId, adminNotes, paymentMode = "wallet", depositAmount }) =>
  async (dispatch, getState) => {
    try {
      const config = getAuthConfig(getState);
      const { data } = await axios.post(
        `${baseUrl}/wallet/admin/spend`,
        { userId, items, clubId, adminNotes, paymentMode, depositAmount },
        config,
      );
      return data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
      };
    }
  };

// A player's table time at a club: running stays and unpaid bills, with the total due
export const getPlayerTableTime =
  (playerId, clubId) => async (dispatch, getState) => {
    try {
      const config = getAuthConfig(getState);
      const { data } = await axios.get(
        `${baseUrl}/table-bills/player/${playerId}/table-time?clubId=${clubId}`,
        config,
      );
      return data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
      };
    }
  };

export const createAdminTransaction =
  (
    userId,
    transactionType,
    amount,
    clubId = null,
    adminNotes = "",
    paymentStatus = null,
    cashoutMode = null,
    bonusAmount = 0,
  ) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: CREATE_ADMIN_TRANSACTION_REQUEST,
      });

      const config = getAuthConfig(getState);

      // Build request body - only include clubId if provided
      const requestBody = {
        userId,
        transactionType,
        amount,
        bonusAmount,
        adminNotes, // Add adminNotes to request (mandatory)
      };

      // Only add clubId if it's provided (for super admins or multi-club admins)
      if (clubId) {
        requestBody.clubId = clubId;
      }

      // Add paymentStatus for Cash Deposit into Wallet
      if (paymentStatus) {
        requestBody.paymentStatus = paymentStatus;
      }

      // Add cashoutMode for Cashout transactions
      if (cashoutMode) {
        requestBody.cashoutMode = cashoutMode;
      }

      const { data } = await axios.post(
        `${baseUrl}/wallet/admin/create-transaction`,
        requestBody,
        config,
      );

      dispatch({
        type: CREATE_ADMIN_TRANSACTION_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: CREATE_ADMIN_TRANSACTION_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const getUsersForTransaction = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_USERS_FOR_TRANSACTION_REQUEST,
    });

    const config = getAuthConfig(getState);
    const { data } = await axios.get(`${baseUrl}/wallet/admin/users`, config);

    dispatch({
      type: GET_USERS_FOR_TRANSACTION_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: GET_USERS_FOR_TRANSACTION_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const getWalletBalance = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_WALLET_BALANCE_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.get(`${baseUrl}/wallet/balance`, config);

    dispatch({
      type: GET_WALLET_BALANCE_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: GET_WALLET_BALANCE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const getUserTransactions =
  (page = 1, limit = 10, type = "", status = "") =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: GET_USER_TRANSACTIONS_REQUEST,
      });

      const {
        userLogin: { userInfo },
      } = getState();

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      let url = `${baseUrl}/wallet/transactions?page=${page}&limit=${limit}`;
      if (type) url += `&type=${type}`;
      if (status) url += `&status=${status}`;

      const { data } = await axios.get(url, config);

      dispatch({
        type: GET_USER_TRANSACTIONS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: GET_USER_TRANSACTIONS_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const createDeposit =
  (amount, paymentMethod, referenceId) => async (dispatch, getState) => {
    try {
      dispatch({
        type: CREATE_DEPOSIT_REQUEST,
      });

      const {
        userLogin: { userInfo },
      } = getState();

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.post(
        `${baseUrl}/wallet/deposit`,
        {
          amount,
          paymentMethod,
          referenceId,
        },
        config,
      );

      dispatch({
        type: CREATE_DEPOSIT_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: CREATE_DEPOSIT_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const createChipDeposit =
  (amount, paymentMethod, referenceId) => async (dispatch, getState) => {
    // ✅ CHANGED function name
    try {
      dispatch({
        type: CREATE_CHIP_DEPOSIT_REQUEST, // ✅ CHANGED
      });

      const {
        userLogin: { userInfo },
      } = getState();

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.post(
        `${baseUrl}/wallet/chip-deposit`, // ✅ CHANGED endpoint
        {
          amount,
          paymentMethod,
          referenceId,
        },
        config,
      );

      dispatch({
        type: CREATE_CHIP_DEPOSIT_SUCCESS, // ✅ CHANGED
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: CREATE_CHIP_DEPOSIT_FAIL, // ✅ CHANGED
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const createBuyIn = (amount, gameId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: CREATE_BUYIN_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(
      `${baseUrl}/wallet/buy-in`,
      {
        amount,
        gameId,
      },
      config,
    );

    dispatch({
      type: CREATE_BUYIN_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: CREATE_BUYIN_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const createCashout = (amount, gameId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: CREATE_CASHOUT_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(
      `${baseUrl}/wallet/cashout`,
      {
        amount,
        gameId,
      },
      config,
    );

    dispatch({
      type: CREATE_CASHOUT_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: CREATE_CASHOUT_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const getAllTransactions =
  (page = 1, limit = 20, type = "", status = "", userId = "", dates = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: GET_ALL_TRANSACTIONS_REQUEST,
      });

      const config = getAuthConfig(getState);
      let url = `${baseUrl}/wallet/admin/transactions?page=${page}&limit=${limit}`;
      if (type) url += `&type=${type}`;
      if (status) url += `&status=${status}`;
      if (userId) url += `&userId=${userId}`;
      url += dateRangeParams(dates);

      const { data } = await axios.get(url, config);

      dispatch({
        type: GET_ALL_TRANSACTIONS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: GET_ALL_TRANSACTIONS_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const updateTransactionStatus =
  (transactionId, status, rejectedReason) => async (dispatch, getState) => {
    try {
      dispatch({
        type: UPDATE_TRANSACTION_STATUS_REQUEST,
      });

      const config = getAuthConfig(getState);
      const { data } = await axios.put(
        `${baseUrl}/wallet/admin/transaction/${transactionId}/status`,
        {
          status,
          rejectedReason,
        },
        config,
      );

      dispatch({
        type: UPDATE_TRANSACTION_STATUS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: UPDATE_TRANSACTION_STATUS_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

// Mark an unpaid deposit as paid
export const markDepositAsPaid =
  (transactionId) => async (dispatch, getState) => {
    try {
      dispatch({
        type: UPDATE_TRANSACTION_STATUS_REQUEST,
      });

      const config = getAuthConfig(getState);
      const { data } = await axios.put(
        `${baseUrl}/wallet/admin/transaction/${transactionId}/status`,
        {
          markAsPaid: true,
        },
        config,
      );

      dispatch({
        type: UPDATE_TRANSACTION_STATUS_SUCCESS,
        payload: data,
      });

      return { success: true, data };
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return { success: false, error: "Unauthorized" };
      }
      dispatch({
        type: UPDATE_TRANSACTION_STATUS_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

// Download transaction receipt
export const downloadTransactionReceipt =
  (transactionId) => async (dispatch, getState) => {
    try {
      const token = getAuthToken(getState);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob", // Important for file download
      };

      const { data } = await axios.get(
        `${baseUrl}/wallet/admin/transaction/${transactionId}/receipt`,
        config,
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipt-${transactionId}.pdf`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return { success: false, error: "Unauthorized" };
      }
      console.error("Failed to download receipt:", error);
      return {
        success: false,
        error:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      };
    }
  };

// Get Club-wise User Ledger (Admin)
export const getClubLedger =
  ({ userId, clubId, startDate, endDate, page = 1, limit = 20 }) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: GET_CLUB_LEDGER_REQUEST,
      });

      const config = getAuthConfig(getState);
      let url = `${baseUrl}/wallet/admin/club-ledger?page=${page}&limit=${limit}`;
      if (userId) url += `&userId=${userId}`;
      if (clubId) url += `&clubId=${clubId}`;
      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;

      const { data } = await axios.get(url, config);

      dispatch({
        type: GET_CLUB_LEDGER_SUCCESS,
        payload: data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return;
      }
      dispatch({
        type: GET_CLUB_LEDGER_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };
